/**
 * UniversalConnectionParameter
 *
 * Dynamic field renderer for connection parameters based on parameter type.
 * Renders appropriate Fluent UI control based on parameter definition.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Field,
  Dropdown,
  Option,
  Checkbox,
  SpinButton,
  Text,
  Button,
  Tooltip,
  type SpinButtonOnChangeData,
} from '@fluentui/react-components';
import {
  EyeOffRegular,
  EyeRegular,
  InfoRegular,
  KeyRegular,
} from '@fluentui/react-icons';
import {
  type ConnectionParameter,
  type FormFieldValue,
  type FieldValidationError,
  ParameterType,
  getParameterDisplayName,
  isParameterRequired,
} from '@/types/connectionParameters';
import { ClientCertificateInput, type ClientCertificateValues } from './ClientCertificateInput';

const useStyles = makeStyles({
  field: {
    marginBottom: tokens.spacingVerticalS,
  },
  tooltip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  tooltipIcon: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
  },
  oauthButton: {
    width: '100%',
  },
  oauthSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  oauthText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
  passwordInput: {
    // Hide the browser's native password reveal button (Edge/Chrome) to avoid
    // a duplicate toggle next to our custom show/hide button.
    '& input::-ms-reveal': { display: 'none' },
    '& input::-webkit-credentials-auto-fill-button': { display: 'none' },
  },
  managedIdentitySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },

});

export interface UniversalConnectionParameterProps {
  /** Parameter key (name) */
  parameterKey: string;
  /** Parameter definition from manifest */
  parameter: ConnectionParameter;
  /** Current value */
  value: FormFieldValue;
  /** Change handler */
  onChange: (key: string, value: FormFieldValue) => void;
  /** Validation error if any */
  error?: FieldValidationError;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Handler for OAuth button click */
  onOAuthClick?: () => void;
  /** Whether OAuth is in progress */
  isOAuthInProgress?: boolean;
}

/**
 * Renders a connection parameter field based on its type
 */
export const UniversalConnectionParameter = ({
  parameterKey,
  parameter,
  value,
  onChange,
  error,
  disabled = false,
  onOAuthClick,
  isOAuthInProgress = false,
}: UniversalConnectionParameterProps) => {
  const styles = useStyles();
  const [showPassword, setShowPassword] = useState(false);


  const displayName = useMemo(
    () => getParameterDisplayName(parameterKey, parameter),
    [parameterKey, parameter]
  );

  const isRequired = useMemo(
    () => isParameterRequired(parameter),
    [parameter]
  );

  // Description can live at uiDefinition.description or uiDefinition.schema.description
  const description = useMemo(() => {
    const ui = parameter.uiDefinition;
    if (ui?.description) return ui.description;
    // Some Azure connectors (e.g. Tenant) nest the description under schema
    const schema = (ui as Record<string, unknown> | undefined)?.schema as
      | Record<string, unknown>
      | undefined;
    if (typeof schema?.description === 'string') return schema.description;
    return undefined;
  }, [parameter.uiDefinition]);
  const tooltip = parameter.uiDefinition?.tooltip;
  const constraints = parameter.uiDefinition?.constraints;
  const allowedValues = constraints?.allowedValues;

  // Placeholder shown inside text inputs: prefer tooltip (usually an example),
  // then description, then a generated "Enter <label>" fallback so every field has one.
  const placeholderText = tooltip ?? description ?? `Enter ${displayName}`;

  // Show the info icon only when there is extra context beyond what the placeholder
  // already communicates: either a description that differs from the placeholder,
  // or the placeholder is long enough (>80 chars) that it will be truncated in the input.
  const infoContent = useMemo(() => {
    // If there's a description that is different from placeholder, show it
    if (description && description !== placeholderText) return description;
    // If placeholder is long it will be clipped — surface it in the info icon too
    if (placeholderText.length > 80) return placeholderText;
    return null;
  }, [description, placeholderText]);

  // Handle value change
  const handleChange = useCallback(
    (newValue: FormFieldValue) => {
      onChange(parameterKey, newValue);
    },
    [onChange, parameterKey]
  );

  // Build label with optional info icon.
  // The info icon only appears when there is extra context beyond the placeholder.
  // Note: the required asterisk is always handled by Field's `required` prop so that
  // aria-required semantics are preserved for assistive technologies.
  const renderLabel = () => {
    if (!infoContent) {
      return displayName;
    }

    return (
      <span className={styles.tooltip}>
        {displayName}
        <Tooltip content={infoContent} relationship="description">
          <InfoRegular className={styles.tooltipIcon} />
        </Tooltip>
      </span>
    );
  };

  // Render based on parameter type
  const renderInput = () => {
    const type = parameter.type;

    // Handle OAuth settings
    if (type === ParameterType.OAuthSetting) {
      return (
        <div className={styles.oauthSection}>
          <Text className={styles.oauthText}>
            This connection requires authentication. Click the button below to sign in.
          </Text>
          <Button
            appearance="primary"
            icon={<KeyRegular />}
            onClick={onOAuthClick}
            disabled={disabled || isOAuthInProgress}
            className={styles.oauthButton}
            data-testid="oauth-sign-in-button"
          >
            {isOAuthInProgress ? 'Authenticating...' : 'Sign in'}
          </Button>
        </div>
      );
    }

    // Handle managed identity
    if (type === ParameterType.ManagedIdentity) {
      return (
        <div className={styles.managedIdentitySection}>
          <Text className={styles.oauthText}>
            This connection will use managed identity for authentication.
          </Text>
        </div>
      );
    }

    // Handle client certificate (PFX file upload + optional password)
    if (type === ParameterType.ClientCertificate || type === 'clientCertificate') {
      const certValue: Partial<ClientCertificateValues> = (value && typeof value === 'object' && !Array.isArray(value))
        ? value as Partial<ClientCertificateValues>
        : { pfx: '', password: '' };

      return (
        <ClientCertificateInput
          values={certValue}
          onChange={(newValues) => handleChange(newValues as FormFieldValue)}
          disabled={disabled}
        />
      );
    }

    // Handle dropdown (allowed values)
    if (allowedValues && allowedValues.length > 0) {
      const selectedValue = value?.toString() ?? '';
      return (
        <Dropdown
          value={
            allowedValues.find((av) => av.value === value)?.text ??
            selectedValue
          }
          selectedOptions={[selectedValue]}
          onOptionSelect={(_e, data) => {
            const selectedOption = allowedValues.find(
              (av) => av.value?.toString() === data.optionValue
            );
            // Convert to FormFieldValue - use string or the original value
            const newValue = selectedOption?.value ?? data.optionValue ?? '';
            handleChange(typeof newValue === 'object' ? String(newValue) : newValue as FormFieldValue);
          }}
          disabled={disabled}
        >
          {allowedValues.map((av, idx) => (
            <Option key={idx} value={av.value?.toString() ?? ''}>
              {av.text ?? av.value?.toString() ?? ''}
            </Option>
          ))}
        </Dropdown>
      );
    }

    // Handle boolean
    if (type === ParameterType.Bool) {
      return (
        <Checkbox
          checked={value === true}
          onChange={(_e, data) => handleChange(data.checked === true)}
          disabled={disabled}
          label={displayName}
        />
      );
    }

    // Handle integer
    if (type === ParameterType.Int) {
      const spinButtonProps: {
        value: number;
        onChange: (_e: React.SyntheticEvent<HTMLElement>, data: SpinButtonOnChangeData) => void;
        disabled: boolean;
        min?: number;
        max?: number;
      } = {
        value: typeof value === 'number' ? value : 0,
        onChange: (_e: React.SyntheticEvent<HTMLElement>, data: SpinButtonOnChangeData) => {
          handleChange(data.value ?? 0);
        },
        disabled,
      };
      if (constraints?.minimum !== undefined) {
        spinButtonProps.min = constraints.minimum;
      }
      if (constraints?.maximum !== undefined) {
        spinButtonProps.max = constraints.maximum;
      }
      return <SpinButton {...spinButtonProps} />;
    }

    // Handle secure string (password)
    // Use a plain text input with a toggle icon inside (contentAfter) to avoid
    // the browser's native reveal button that creates a duplicate toggle.
    if (type === ParameterType.SecureString) {
      return (
        <Input
          className={styles.passwordInput}
          type={showPassword ? 'text' : 'password'}
          value={typeof value === 'string' ? value : ''}
          onChange={(_e, data) => handleChange(data.value)}
          disabled={disabled}
          autoComplete="new-password"
          placeholder={placeholderText}
          contentAfter={
            <Button
              appearance="subtle"
              size="small"
              icon={showPassword ? <EyeOffRegular /> : <EyeRegular />}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide value' : 'Show value'}
            />
          }
          data-testid={`connection-param-${parameterKey}`}
        />
      );
    }

    // Default: string input
    return (
      <Input
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(_e, data) => handleChange(data.value)}
        disabled={disabled}
        placeholder={placeholderText}
        data-testid={`connection-param-${parameterKey}`}
      />
    );
  };

  // For boolean, render without Field wrapper
  if (parameter.type === ParameterType.Bool) {
    return (
      <div className={styles.field}>
        {renderInput()}
      </div>
    );
  }

  // For OAuth and managed identity, render without Field wrapper
  // (they manage their own internal layout and explanatory text)
  if (
    parameter.type === ParameterType.OAuthSetting ||
    parameter.type === ParameterType.ManagedIdentity
  ) {
    return <div className={styles.field}>{renderInput()}</div>;
  }

  // Client certificate needs Field wrapper for label and required indicator
  if (
    parameter.type === ParameterType.ClientCertificate ||
    parameter.type === 'clientCertificate'
  ) {
    const certFieldProps: {
      className: string;
      label: string | React.ReactElement;
      required?: boolean;
      validationState?: 'error';
      validationMessage?: string;
    } = {
      className: styles.field,
      label: renderLabel(),
      required: isRequired,
    };

    if (error) {
      certFieldProps.validationState = 'error';
      certFieldProps.validationMessage = error.message;
    }

    return (
      <Field {...certFieldProps}>
        {renderInput()}
      </Field>
    );
  }

  const fieldProps: {
    className: string;
    label: string | React.ReactElement;
    required?: boolean;
    validationState?: 'error';
    validationMessage?: string;
  } = {
    className: styles.field,
    label: renderLabel(),
    required: isRequired,
  };

  if (error) {
    fieldProps.validationState = 'error';
    fieldProps.validationMessage = error.message;
  }

  return (
    <Field {...fieldProps}>
      {renderInput()}
    </Field>
  );
};

UniversalConnectionParameter.displayName = 'UniversalConnectionParameter';
